/**
 * Segment Mode Workbench — analyze a game in 5-minute windows.
 *
 * Phase 3: zoomed detail timeline with tactical score curve and moment dots,
 * spatial pitch iframe, moment score filter (>=50), fixed pressing index,
 * corner/goal markers on timeline.
 *
 * See docs/segment-mode-design.md for the full plan.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { api, type Session } from '../../lib/api'
import { NO_FRAME_RATE_NOTE, servedFps } from '../../lib/frameRate'
import { getClubGames, getClubs, type CachedClub, type CachedGame } from '../../lib/clubsCache'
import type { DangerosityPoint, DangerosityResponse, MatchPeriod, Moment, TeamMoments } from '../match/types'
import { detectHalftime, getCurrentFilteredMoments, type Halftime } from '../match/filtering'
import {
  dynamicThresholdsFor, HIGH_SCORE, hasNoScore, isHighScore, NO_SCORE_COLOR,
  noScoreReason, performanceColor, scoreText, type ScoreThresholds,
} from '../match/momentScores'
import { deriveCompletedPasses, aggregateNetwork, type NetworkNode, type NetworkEdge } from '../match/teamSplit'
import { TrackingDataCache } from '../match/trackingLoader'
import { PitchControlPanel, WindowHeatPanel, type LiveFrameRef } from '../match/TeamSplitPanels'
import { resolveWindowQuery } from './windowQuery'

import './segment.css'

// ── types ──────────────────────────────────────────────────────────────

interface SegmentWindow { frameStart: number; frameEnd: number }

interface PinnedItem {
  id: string
  kind: 'moment' | 'tag' | 'clip' | 'observation'
  window: SegmentWindow
  windowLabel: string
  summary: string
  frame?: number
  moment?: Moment
  label?: string
  pinnedAt: number
}

interface GameData {
  fps: number
  total_frames: number
  video_url: string | null
  video_game_start_time: number
  home_team: string
  away_team: string
  home_team_color: string | null
  away_team_color: string | null
}

interface TacticalScorePoint { frame: number; tactical_score: number; possession: number }
interface PressingIndexPoint { frame: number; pressing_index: number; pressing_team: number }

const MIN_SCORE_SHOWN = Math.round(HIGH_SCORE * 100) // the list cut as a score is printed

// ── helpers ────────────────────────────────────────────────────────────

function ft(frame: number, fps: number): string {
  const secs = Math.floor(frame / fps)
  return `${Math.floor(secs / 60)}:${(secs % 60).toString().padStart(2, '0')}`
}

function defaultWindow(fps: number): SegmentWindow {
  return { frameStart: 0, frameEnd: Math.floor(5 * 60 * fps) }
}

function wLabel(w: SegmentWindow, fps: number): string {
  return `${ft(w.frameStart, fps)}\u2013${ft(w.frameEnd, fps)}`
}

function windowAroundFrame(
  frame: number, fps: number, dur: number,
  ht: Halftime | null, total: number,
): SegmentWindow {
  const half = Math.floor((dur * 60 * fps) / 2)
  let s = Math.max(0, frame - half), e = Math.min(total, frame + half)
  if (ht) { if (frame < ht.startFrame) e = Math.min(e, ht.startFrame); else s = Math.max(s, ht.endFrame) }
  return { frameStart: s, frameEnd: e }
}

/** Playhead at a once-per-second tick — enough for the building panels,
 *  cheap enough to recompute a heat grid and a pass network on each. */
function useTickedFrame(frameRef: LiveFrameRef): number {
  const [frame, setFrame] = useState(0)
  useEffect(() => {
    const id = setInterval(() => {
      setFrame(prev => (frameRef.current.frame !== prev ? frameRef.current.frame : prev))
    }, 1000)
    return () => clearInterval(id)
  }, [frameRef])
  return frame
}

/** Copilot retries while a match's data is being built (503). */
const OBS_TRIES = 6

/** One copilot observation from the segment-observations endpoint. */
interface Observation {
  kind: string
  team: number | null
  text: string
}

// ── pass network SVG ───────────────────────────────────────────────────

/** The live cards fill in as the video runs. Before the playhead moves,
 *  "No passes." states a finding about the match when all it means is
 *  that nothing has been played yet — the same mistake the dashboard's
 *  blank panels made. */
const LIVE_NOT_YET = 'Press play — this fills in as the video runs.'

const NS = 3, NW = 105 * NS, NH = 68 * NS
const npx = (x: number) => (x + 52.5) * NS
const npy = (y: number) => (34 - y) * NS

function PassNetSVG({ nodes, edges, color, emptyNote }: {
  nodes: NetworkNode[]; edges: NetworkEdge[]; color: string
  /** Shown instead of "No passes." where empty means "not yet", not
   *  "none happened" — the live card before the playhead has moved. */
  emptyNote?: string
}) {
  if (!nodes.length) {
    return <p className="seg-empty">{emptyNote ?? 'No passes.'}</p>
  }
  const me = Math.max(...edges.map(e => e.n), 1)
  const mt = Math.max(...nodes.map(n => n.touches), 1)
  return (
    <svg viewBox={`0 0 ${NW} ${NH}`} className="seg-pass-net-svg">
      <rect width={NW} height={NH} rx={4} fill="var(--color-surface-sunken, #1a1a1a)" />
      <line x1={NW / 2} y1={0} x2={NW / 2} y2={NH} stroke="var(--color-border, #333)" strokeWidth={0.5} />
      {edges.map((e, i) => {
        const a = nodes.find(n => n.jersey === e.from), b = nodes.find(n => n.jersey === e.to)
        return a && b ? <line key={i} x1={npx(a.x)} y1={npy(a.y)} x2={npx(b.x)} y2={npy(b.y)}
          stroke={color} strokeWidth={0.8 + 3 * (e.n / me)} strokeOpacity={0.2 + 0.6 * (e.n / me)} /> : null
      })}
      {nodes.map(n => (
        <g key={n.jersey}>
          <circle cx={npx(n.x)} cy={npy(n.y)} r={5 + 4 * (n.touches / mt)}
                  fill={color} fillOpacity={0.85} stroke="rgba(0,0,0,0.4)" strokeWidth={0.5} />
          <text x={npx(n.x)} y={npy(n.y) + 3} textAnchor="middle" fill="#fff" fontSize={7} fontWeight={600}>{n.jersey}</text>
        </g>
      ))}
    </svg>
  )
}

// ── zoomed detail timeline SVG ─────────────────────────────────────────

function DetailTimeline({ window: w, moments, thresholds, tacticalScore, pressingIndex, corners, goals, fps, team0Color, team1Color, onSeek }: {
  window: SegmentWindow
  moments: Moment[]
  thresholds: ScoreThresholds
  tacticalScore: TacticalScorePoint[] | null
  pressingIndex: PressingIndexPoint[] | null
  corners: Moment[]
  goals: Moment[]
  fps: number
  team0Color: string; team1Color: string
  onSeek: (frame: number) => void
}) {
  // Measured width: a fixed viewBox under width:100% letterboxes the
  // drawing to the middle of a wide screen; drawing in real pixels fills it.
  const wrapRef = useRef<HTMLDivElement>(null)
  const [wpx, setWpx] = useState(800)
  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(es => {
      const mw = es[0]?.contentRect.width ?? 0
      if (mw > 0) setWpx(Math.round(mw))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const W = wpx, H = 80, PAD = 4
  const span = w.frameEnd - w.frameStart
  const x = (frame: number) => PAD + ((frame - w.frameStart) / span) * (W - 2 * PAD)

  // Tactical score curve (possession=1 = home)
  const tsHome = (tacticalScore ?? []).filter(p => p.possession === 1 && p.frame >= w.frameStart && p.frame <= w.frameEnd)
  const tsAway = (tacticalScore ?? []).filter(p => p.possession === 0 && p.frame >= w.frameStart && p.frame <= w.frameEnd)
  const maxTS = Math.max(...[...tsHome, ...tsAway].map(p => Math.abs(p.tactical_score)), 1)

  const curvePath = (pts: TacticalScorePoint[], baseline: number) => {
    if (pts.length < 2) return ''
    return 'M' + pts.map(p => `${x(p.frame)},${baseline - (p.tactical_score / maxTS) * (H / 2 - 10)}`).join('L')
  }

  // Pressing index curve
  const piPts = (pressingIndex ?? []).filter(p => p.frame >= w.frameStart && p.frame <= w.frameEnd)
  const piPath = piPts.length >= 2
    ? 'M' + piPts.map(p => `${x(p.frame)},${H - 6 - p.pressing_index * (H / 3)}`).join('L')
    : ''

  // Minute markers
  const mins: number[] = []
  for (let f = w.frameStart; f <= w.frameEnd; f += fps * 60) {
    mins.push(f)
  }

  return (
    <div ref={wrapRef} className="seg-detail-wrap">
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="seg-detail-timeline"
         onClick={e => {
           const rect = (e.target as SVGElement).closest('svg')!.getBoundingClientRect()
           const pct = (e.clientX - rect.left) / rect.width
           onSeek(Math.floor(w.frameStart + pct * span))
         }}>
      <rect width={W} height={H} rx={4} fill="var(--color-surface-sunken, #1a1a1a)" />

      {/* Minute markers */}
      {mins.map(f => (
        <g key={f}>
          <line x1={x(f)} y1={0} x2={x(f)} y2={H} stroke="var(--color-border, #333)" strokeWidth={0.5} />
          <text x={x(f) + 2} y={10} fill="var(--color-text-secondary, #999)" fontSize={8}>{ft(f, fps)}</text>
        </g>
      ))}

      {/* Tactical score curves */}
      {tsHome.length >= 2 && <path d={curvePath(tsHome, H / 2)} fill="none" stroke={team1Color} strokeWidth={1.5} strokeOpacity={0.7} />}
      {tsAway.length >= 2 && <path d={curvePath(tsAway, H / 2)} fill="none" stroke={team0Color} strokeWidth={1.5} strokeOpacity={0.7} />}

      {/* Pressing index curve */}
      {piPath && <path d={piPath} fill="none" stroke="#f59e0b" strokeWidth={1} strokeOpacity={0.5} strokeDasharray="3,2" />}

      {/* Corner markers — flag icon ⛳ */}
      {corners.filter(c => (c.frame ?? 0) >= w.frameStart && (c.frame ?? 0) <= w.frameEnd).map((c, i) => (
        <g key={`c${i}`} transform={`translate(${x(c.frame ?? 0)}, ${H - 16})`}>
          <line x1={0} y1={0} x2={0} y2={10} stroke="#a855f7" strokeWidth={1} />
          <path d="M0,0 L6,2 L0,4 Z" fill="#a855f7" fillOpacity={0.9} />
        </g>
      ))}

      {/* Goal markers — ⚽ */}
      {goals.filter(g => (g.frame ?? 0) >= w.frameStart && (g.frame ?? 0) <= w.frameEnd).map((g, i) => (
        <text key={`g${i}`} x={x(g.frame ?? 0)} y={14} textAnchor="middle"
              fontSize={12} fill="#fff" style={{ cursor: 'pointer' }}
              onClick={e => { e.stopPropagation(); onSeek(g.frame ?? 0) }}>
          {'\u26BD'}
        </text>
      ))}

      {/* Moment dots */}
      {moments.map((m, i) => {
        const f = m.frame ?? m.start_frame ?? 0
        const noScore = hasNoScore(m)
        return (
          <circle key={i} cx={x(f)} cy={H / 2} r={3}
                  fill={noScore ? undefined : performanceColor(m.score ?? 0, thresholds)}
                  style={noScore ? { fill: NO_SCORE_COLOR } : undefined} fillOpacity={0.8}
                  stroke="rgba(0,0,0,0.3)" strokeWidth={0.5}>
            {noScore && <title>{noScoreReason(m)}</title>}
          </circle>
        )
      })}
    </svg>
    </div>
  )
}

// ── main component ─────────────────────────────────────────────────────

export default function SegmentWorkbench({
  session, gameCode: externalGameCode, orgId: externalOrgId,
}: { session: Session; gameCode?: string | null; orgId?: number | null }) {
  const isStaff = session.user.is_staff || session.user.is_platform_admin

  const [clubs, setClubs] = useState<CachedClub[] | null>(null)
  const [selectedClub, setSelectedClub] = useState<CachedClub | null>(null)
  const [clubGames, setClubGames] = useState<CachedGame[] | null>(null)
  const [internalGameCode, setInternalGameCode] = useState<string | null>(null)
  const gameCode = externalGameCode ?? internalGameCode
  const setGameCode = externalGameCode ? () => {} : setInternalGameCode

  const [gameData, setGameData] = useState<GameData | null>(null)
  const [homeMoments, setHomeMoments] = useState<TeamMoments | null>(null)
  const [awayMoments, setAwayMoments] = useState<TeamMoments | null>(null)
  const [dangerosityData, setDangerosityData] = useState<DangerosityPoint[] | null>(null)
  const [matchPeriods, setMatchPeriods] = useState<MatchPeriod[] | null>(null)
  const [passPotentialData, setPassPotentialData] = useState<Record<number, { frame: number; team: number; jersey: number; x: number; y: number }> | null>(null)
  const [tacticalScoreData, setTacticalScoreData] = useState<TacticalScorePoint[] | null>(null)
  const [pressingIndexData, setPressingIndexData] = useState<PressingIndexPoint[] | null>(null)
  const [cornerMoments, setCornerMoments] = useState<Moment[]>([])
  const [goalMoments, setGoalMoments] = useState<Moment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [segWindow, setSegWindow] = useState<SegmentWindow | null>(null)
  const [pins, setPins] = useState<PinnedItem[]>([])
  const videoRef = useRef<HTMLVideoElement>(null)
  const [tagInput, setTagInput] = useState('')
  const [showTagInput, setShowTagInput] = useState(false)

  // ── copilot observations (Phase 3) ──
  const [observations, setObservations] = useState<Observation[] | null>(null)
  const [obsLoading, setObsLoading] = useState(false)
  /** Why there is nothing to show, when it is not "nothing cleared a
   *  threshold": the game has no event data, or the read failed. */
  const [obsNote, setObsNote] = useState<string | null>(null)
  /** Related-moment suggestion raised when the analyst pins a moment. */
  const [relatedSuggestion, setRelatedSuggestion] = useState<{ text: string; frame: number } | null>(null)

  // ── NL window selection (Phase 4) ──
  const [askInput, setAskInput] = useState('')
  const [askChip, setAskChip] = useState<string | null>(null)

  /** Share-link state parsed from the URL hash, applied once after load. */
  const sharedStateRef = useRef<{ g: string; s: number; e: number; pins?: PinnedItem[] } | null>(null)

  // ── tracking data for spatial overlays ──
  const [trackingCache, setTrackingCache] = useState<TrackingDataCache | null>(null)
  const liveFrameRef = useRef({ frame: 0, at: 0 })
  const liveFrame = useTickedFrame(liveFrameRef)

  // ── share-link hash (Phase 4): #seg=<urlencoded JSON {g,s,e,pins}> ──
  useEffect(() => {
    const m = /(?:^|[#&])seg=([^&]+)/.exec(window.location.hash)
    if (!m) return
    try {
      const st = JSON.parse(decodeURIComponent(m[1]!)) as { g?: unknown; s?: unknown; e?: unknown; pins?: PinnedItem[] }
      if (typeof st.g === 'string' && typeof st.s === 'number' && typeof st.e === 'number') {
        sharedStateRef.current = { g: st.g, s: st.s, e: st.e, pins: st.pins }
        if (!externalGameCode) setInternalGameCode(st.g)
      }
    } catch { /* malformed hash — ignore */ }
  }, [externalGameCode])

  useEffect(() => { if (isStaff) getClubs().then(setClubs).catch(() => {}) }, [isStaff])
  useEffect(() => { if (!selectedClub) { setClubGames(null); return }; getClubGames(selectedClub.id).then(setClubGames).catch(() => {}) }, [selectedClub])

  // ── load all data ──
  // The game-match-summary endpoints are org-scoped: the session must be in the
  // game's club or the server returns 404. Switch before loading, same pattern
  // as ManageGames.enterClubOf.
  useEffect(() => {
    if (!gameCode) {
      setGameData(null); setHomeMoments(null); setAwayMoments(null); setSegWindow(null)
      setPassPotentialData(null); setTacticalScoreData(null); setPressingIndexData(null)
      setCornerMoments([]); setGoalMoments([])
      if (trackingCache) { trackingCache.destroy(); setTrackingCache(null) }
      return
    }
    setLoading(true); setError(null)

    const switchOrgId = selectedClub?.id ?? externalOrgId
    const switchClub = switchOrgId
      ? api('/api/v1/auth/switch-organization', {
          method: 'POST',
          body: JSON.stringify({ organization_id: switchOrgId }),
        }).catch(() => {/* best-effort: may already be in this club */})
      : Promise.resolve()

    switchClub.then(() => Promise.all([
      api<Record<string, unknown>>(`/api/v1/game-match-summary/game/${gameCode}`),
      api<Record<string, unknown>>(`/api/v1/game-match-summary/moments/${gameCode}`),
      api<Record<string, unknown>>(`/api/v1/game-match-summary/dangerosity/${gameCode}`),
      api<Record<string, unknown>>(`/api/v1/game-match-summary/pass-potential/${gameCode}`).catch(() => null),
      api<Record<string, unknown>>(`/api/v1/game-match-summary/tactical-score/${gameCode}`).catch(() => null),
      api<Record<string, unknown>>(`/api/v1/game-match-summary/pressing-index/${gameCode}`).catch(() => null),
      api<Record<string, unknown>>(`/api/v1/game-match-summary/corner-moments/${gameCode}`).catch(() => null),
    ]).then(([gameResp, momentsResp, dangResp, ppResp, tsResp, piResp, cornResp]) => {
      const raw = gameResp as Record<string, unknown>
      const g = (raw.game ?? raw) as Record<string, unknown>
      // the game's recorded rate; with none nothing on this page can be
      // placed in time, so it says so (the error state) rather than guess 30
      const fps = servedFps(g.fps)
      if (fps === null) { setError(NO_FRAME_RATE_NOTE); setLoading(false); return }
      const totalFrames = (g.total_frames as number) || 0
      setGameData({
        fps, total_frames: totalFrames,
        video_url: (g.video_url as string) || null,
        video_game_start_time: (g.video_game_start_time as number) || 0,
        home_team: (g.home_team_name as string) || 'Home',
        away_team: (g.away_team_name as string) || 'Away',
        home_team_color: (g.home_team_color as string) || null,
        away_team_color: (g.away_team_color as string) || null,
      })
      const mRaw = momentsResp as Record<string, unknown>
      const m = (mRaw.moments ?? mRaw) as Record<string, unknown>
      setHomeMoments((m.home as TeamMoments) ?? null)
      setAwayMoments((m.away as TeamMoments) ?? null)
      // Goals come from annotations. The API shapes `type` as `kind_outcome`
      // ('shot_goal', 'penalty_goal', 'corner_goal'), so match the outcome —
      // a bare 'shot' is an outcome-less shot, not a goal.
      const annotations = (m.annotations ?? []) as Moment[]
      setGoalMoments(annotations.filter(a => (a.type ?? a.event_type ?? '').endsWith('_goal')))
      setDangerosityData(((dangResp as Record<string, unknown>).data as DangerosityPoint[]) ?? null)
      setMatchPeriods((((dangResp as Record<string, unknown>).metadata as DangerosityResponse['metadata'])?.periods) ?? null)

      if (ppResp) {
        const ppArr = ((ppResp as Record<string, unknown>).moments ?? []) as Array<{ frame: number; team: number; jersey: number; x: number; y: number }>
        const byFrame: Record<number, { frame: number; team: number; jersey: number; x: number; y: number }> = {}
        for (const p of ppArr) byFrame[p.frame] = p
        setPassPotentialData(byFrame)
      }
      if (tsResp) setTacticalScoreData(((tsResp as Record<string, unknown>).data ?? []) as TacticalScorePoint[])
      if (piResp) setPressingIndexData(((piResp as Record<string, unknown>).data ?? []) as PressingIndexPoint[])
      if (cornResp) setCornerMoments(((cornResp as Record<string, unknown>).moments ?? []) as Moment[])

      setSegWindow(defaultWindow(fps))
      setLoading(false)
      try { const s = localStorage.getItem(`segment-pins:${gameCode}`); if (s) setPins(JSON.parse(s)); else setPins([]) } catch { setPins([]) }

      // A shared link overrides the default window (and pins) once.
      const shared = sharedStateRef.current
      if (shared && shared.g === gameCode) {
        setSegWindow({ frameStart: shared.s, frameEnd: shared.e })
        if (shared.pins?.length) setPins(shared.pins)
        sharedStateRef.current = null
      }

      // Initialize tracking data cache (async, loads in background)
      const cache = new TrackingDataCache()
      cache.initialize(gameCode!, totalFrames).catch(() => {})
      setTrackingCache(cache)
    }).catch(e => { setError(e instanceof Error ? e.message : String(e)); setLoading(false) }))
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedClub is read but should not re-trigger
  }, [gameCode])

  useEffect(() => { if (gameCode) localStorage.setItem(`segment-pins:${gameCode}`, JSON.stringify(pins)) }, [pins, gameCode])

  // ── copilot observations (Phase 3): refetch when the window settles ──
  // A stack game's first visit builds its match-wide data (503 while it
  // does): retried, saying so. No data and a failed read each say what
  // happened — never "Nothing significant", which is a finding.
  useEffect(() => {
    if (!gameCode || !segWindow) { setObservations(null); setObsNote(null); return }
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    // never the previous window's observations under this window's heading
    setObservations(null)
    setObsLoading(true)
    setObsNote(null)
    // backing off (3, 6, 12, 24 s, then 30 s): a server shedding load is not
    // asked again every few seconds by every open window
    const delay = (tries: number) => Math.min(30000, 3000 * 2 ** (OBS_TRIES - tries))
    const load = (tries: number) => {
      api<Record<string, unknown>>(`/api/v1/game-match-summary/segment-observations/${gameCode}?start=${segWindow.frameStart}&end=${segWindow.frameEnd}`)
        .then(r => {
          if (cancelled) return
          const body = r as Record<string, unknown>
          setObservations((body.observations ?? []) as Observation[])
          setObsNote(body.available === false ? 'No event data for this match yet.' : null)
          setObsLoading(false)
        })
        .catch((e: unknown) => {
          if (cancelled) return
          const status = (e as { status?: number } | null)?.status
          if (status === 503 && tries > 0) {
            setObsNote('Building this match’s data — the first visit can take a minute…')
            timer = setTimeout(() => load(tries - 1), delay(tries))
            return
          }
          setObservations(null)
          setObsNote(status === 503
            ? 'Copilot is still building this match’s data — try this window again in a minute.'
            : 'Copilot couldn’t read this match’s data.')
          setObsLoading(false)
        })
    }
    timer = setTimeout(() => load(OBS_TRIES), 400)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [gameCode, segWindow])

  // gameData carries the recorded rate; until it lands there is no rate
  // (undefined), the page shows Preparing… and nothing is placed
  const fps = gameData?.fps
  const halftime = useMemo(
    () => dangerosityData ? detectHalftime(dangerosityData, matchPeriods) : null,
    [dangerosityData, matchPeriods],
  )

  // ── ALL moments (both teams, no score filter) for timeline ──
  const allMoments = useMemo(() => {
    if (!homeMoments) return []
    const types = new Set(['progressive_passes', 'supportive_passes', 'progressive_runs', 'supportive_runs', 'regains', 'pressing'])
    const h = getCurrentFilteredMoments({ viewingAsTeam: 1, selectedTeam: 0, activeEventFilters: types, activeFieldPhases: new Set(), activeVerticalZones: new Set(), homeMoments, awayMoments, possessionMoments: null, manualMoments: [], homeTeamStartDirection: null, halftime })
    const a = getCurrentFilteredMoments({ viewingAsTeam: 0, selectedTeam: 0, activeEventFilters: types, activeFieldPhases: new Set(), activeVerticalZones: new Set(), homeMoments, awayMoments, possessionMoments: null, manualMoments: [], homeTeamStartDirection: null, halftime })
    const all = [...h, ...a].sort((a, b) => (a.frame ?? 0) - (b.frame ?? 0))
    const seen = new Set<string>()
    return all.filter(m => { const k = String(m.moment_id ?? m.id ?? `${m.frame}-${m.type}`); if (seen.has(k)) return false; seen.add(k); return true })
  }, [homeMoments, awayMoments, halftime])
  const thresholds = useMemo(() => dynamicThresholdsFor(allMoments), [allMoments])

  // ── window moments (isHighScore) ──
  const windowMoments = useMemo(() => {
    if (!segWindow) return []
    return allMoments.filter(m => {
      const f = m.frame ?? m.start_frame ?? 0
      return f >= segWindow.frameStart && f <= segWindow.frameEnd && isHighScore(m)
    })
  }, [segWindow, allMoments])

  // ── window moments (ALL, for timeline dots) ──
  const windowAllMoments = useMemo(() => {
    if (!segWindow) return []
    return allMoments.filter(m => {
      const f = m.frame ?? m.start_frame ?? 0
      return f >= segWindow.frameStart && f <= segWindow.frameEnd
    })
  }, [segWindow, allMoments])

  const momentBreakdown = useMemo(() => {
    const c: Record<string, number> = {}
    for (const m of windowMoments) c[m.type ?? 'unknown'] = (c[m.type ?? 'unknown'] ?? 0) + 1
    return Object.entries(c).sort((a, b) => b[1] - a[1])
  }, [windowMoments])

  const windowPassNetwork = useMemo(() => {
    if (!passPotentialData || !segWindow) return null
    const pp: Record<number, { frame: number; team: number; jersey: number; x: number; y: number }> = {}
    for (const [f, d] of Object.entries(passPotentialData)) { const n = Number(f); if (n >= segWindow.frameStart && n <= segWindow.frameEnd) pp[n] = d }
    const passes = deriveCompletedPasses(pp)
    return { passes, team0: aggregateNetwork(passes, 0, segWindow.frameEnd), team1: aggregateNetwork(passes, 1, segWindow.frameEnd) }
  }, [passPotentialData, segWindow])

  // ── building views: the window so far, up to the playhead ──
  const liveClamped = segWindow
    ? Math.min(Math.max(liveFrame, segWindow.frameStart), segWindow.frameEnd)
    : 0
  const buildingPassNetwork = useMemo(() => {
    if (!windowPassNetwork) return null
    const upto = windowPassNetwork.passes.filter(p => p.frame <= liveClamped)
    return {
      passes: upto,
      team0: aggregateNetwork(upto, 0, liveClamped),
      team1: aggregateNetwork(upto, 1, liveClamped),
    }
  }, [windowPassNetwork, liveClamped])

  // ── tactical score (window) ──
  const windowTactical = useMemo(() => {
    if (!tacticalScoreData || !segWindow) return null
    const inW = tacticalScoreData.filter(p => p.frame >= segWindow.frameStart && p.frame <= segWindow.frameEnd)
    if (!inW.length) return null
    const home = inW.filter(p => p.possession === 1)
    const away = inW.filter(p => p.possession === 0)
    const avg = (pts: TacticalScorePoint[]) => pts.length ? pts.reduce((s, p) => s + p.tactical_score, 0) / pts.length : 0
    const matchHome = tacticalScoreData.filter(p => p.possession === 1)
    const matchAway = tacticalScoreData.filter(p => p.possession === 0)
    return {
      homeAvg: Math.round(avg(home)), awayAvg: Math.round(avg(away)),
      homeMatchAvg: Math.round(avg(matchHome)), awayMatchAvg: Math.round(avg(matchAway)),
    }
  }, [tacticalScoreData, segWindow])

  // ── pressing index (window) ──
  const windowPressing = useMemo(() => {
    if (!pressingIndexData || !segWindow) return null
    const inW = pressingIndexData.filter(p => p.frame >= segWindow.frameStart && p.frame <= segWindow.frameEnd)
    if (!inW.length) return null
    const avg = inW.reduce((s, p) => s + p.pressing_index, 0) / inW.length
    const matchAvg = pressingIndexData.length ? pressingIndexData.reduce((s, p) => s + p.pressing_index, 0) / pressingIndexData.length : 0
    const regains = windowMoments.filter(m => m.type === 'regains').length
    return { avg: Math.round(avg * 100), matchAvg: Math.round(matchAvg * 100), regains }
  }, [pressingIndexData, segWindow, windowMoments])

  // ── video loop + live frame tracking ──
  const videoOffset = gameData?.video_game_start_time ?? 0
  useEffect(() => {
    const v = videoRef.current; if (!v || !segWindow || fps === undefined) return
    const chk = () => {
      const gameTime = v.currentTime - videoOffset
      const frame = Math.floor(gameTime * fps)
      liveFrameRef.current = { frame, at: performance.now() }
      if (frame >= segWindow.frameEnd) v.currentTime = videoOffset + segWindow.frameStart / fps
    }
    v.addEventListener('timeupdate', chk); return () => v.removeEventListener('timeupdate', chk)
  }, [segWindow, fps, videoOffset])

  const seekToFrame = useCallback((f: number) => { if (videoRef.current && fps !== undefined) videoRef.current.currentTime = videoOffset + f / fps }, [fps, videoOffset])

  // Selecting a window fast-forwards everything to it: whenever the window
  // changes and the playhead sits outside it, seek to the window start. The
  // seek fires timeupdate, which carries the spatial view and the building
  // panels along through liveFrameRef.
  useEffect(() => {
    const v = videoRef.current
    if (!v || !segWindow || fps === undefined) return
    const f = Math.floor((v.currentTime - videoOffset) * fps)
    if (f < segWindow.frameStart || f > segWindow.frameEnd) {
      v.currentTime = videoOffset + segWindow.frameStart / fps
      liveFrameRef.current = { frame: segWindow.frameStart, at: performance.now() }
    }
  }, [segWindow, fps, videoOffset])
  const setWin = useCallback((f: number) => { if (gameData) setSegWindow(windowAroundFrame(f, gameData.fps, 5, halftime, gameData.total_frames)) }, [halftime, gameData])
  const pinMoment = useCallback((m: Moment) => {
    if (!segWindow || fps === undefined) return
    setPins(p => [...p, { id: `m-${m.moment_id ?? Date.now()}`, kind: 'moment', window: segWindow, windowLabel: wLabel(segWindow, fps), summary: `${m.type ?? 'moment'} #${m.player ?? '?'} at ${ft(m.frame ?? 0, fps)}`, frame: m.frame ?? m.start_frame, moment: m, pinnedAt: Date.now() }])
    // Related-moment suggestion (Phase 3): the strongest same-type moment
    // OUTSIDE this window — "this pattern also happened elsewhere".
    const outside = allMoments.filter(x => {
      const f = x.frame ?? x.start_frame ?? 0
      return x.type === m.type && !hasNoScore(x) && (f < segWindow.frameStart || f > segWindow.frameEnd)
    })
    const best = outside.sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0]
    if (best) {
      const f = best.frame ?? best.start_frame ?? 0
      setRelatedSuggestion({ text: `Similar ${String(m.type ?? 'moment').replace(/_/g, ' ')} at ${ft(f, fps)} (score ${scoreText(best)}). Compare?`, frame: f })
    }
  }, [segWindow, fps, allMoments])
  const pinObservation = useCallback((o: Observation) => {
    if (!segWindow || fps === undefined) return
    const teamName = o.team == null || !gameData ? ''
      : `${o.team === 1 ? gameData.home_team : gameData.away_team}: `
    setPins(p => [...p, { id: `o-${Date.now()}`, kind: 'observation', window: segWindow, windowLabel: wLabel(segWindow, fps), summary: teamName + o.text, pinnedAt: Date.now() }])
  }, [segWindow, fps, gameData])
  const pinTag = useCallback((label: string) => { if (!segWindow || !videoRef.current || fps === undefined) return; const f = Math.floor(videoRef.current.currentTime * fps); setPins(p => [...p, { id: `t-${Date.now()}`, kind: 'tag', window: segWindow, windowLabel: wLabel(segWindow, fps), summary: label, frame: f, label, pinnedAt: Date.now() }]) }, [segWindow, fps])
  const unpin = useCallback((id: string) => setPins(p => p.filter(x => x.id !== id)), [])
  const exportPins = useCallback(() => {
    if (!gameCode || !pins.length) return
    const lines = [`Match: ${gameCode}`, '']
    const byW = new Map<string, PinnedItem[]>()
    for (const p of pins) { if (!byW.has(p.windowLabel)) byW.set(p.windowLabel, []); byW.get(p.windowLabel)!.push(p) }
    for (const [w, items] of byW) { lines.push(`Window ${w}:`); for (const i of items) lines.push(`  - [${i.kind}] ${i.summary}`); lines.push('') }
    navigator.clipboard.writeText(lines.join('\n')).catch(() => {})
  }, [pins, gameCode])

  // ── NL window selection (Phase 4) ──
  const runAsk = useCallback(() => {
    const q = askInput.trim()
    if (!q || !gameData) return
    const res = resolveWindowQuery(q, {
      tacticalScore: tacticalScoreData, pressingIndex: pressingIndexData,
      goals: goalMoments, halftime, fps: gameData.fps, totalFrames: gameData.total_frames,
    })
    if (res) {
      setSegWindow({ frameStart: res.frameStart, frameEnd: res.frameEnd })
      setAskChip(res.explanation)
    } else {
      setAskChip(`Could not resolve "${q}" — try "after the first goal", "best pressing spell", "when did we lose territorial control", or "around 34:00".`)
    }
  }, [askInput, gameData, tacticalScoreData, pressingIndexData, goalMoments, halftime])

  // ── share link (Phase 4): window + pins in the URL hash ──
  const shareLink = useCallback(() => {
    if (!gameCode || !segWindow) return
    const st = { g: gameCode, s: segWindow.frameStart, e: segWindow.frameEnd, pins }
    const url = `${window.location.origin}${window.location.pathname}#seg=${encodeURIComponent(JSON.stringify(st))}`
    navigator.clipboard.writeText(url).catch(() => {})
    setAskChip('Share link copied — same match, window, and pins.')
  }, [gameCode, segWindow, pins])

  // ── PDF export (Phase 4): printable pinboard in its own window.
  // window.print of a generated document, not a PDF library — labeled
  // shortcut per the design's open question 4 (PDF-proper is a rendering
  // project; this ships the exportable artifact without a dependency). ──
  const exportPdf = useCallback(() => {
    if (!gameCode || !pins.length || fps === undefined) return
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const byW = new Map<string, PinnedItem[]>()
    for (const p of pins) { if (!byW.has(p.windowLabel)) byW.set(p.windowLabel, []); byW.get(p.windowLabel)!.push(p) }
    const sections = [...byW.entries()].map(([w, items]) => `
      <section>
        <h2>Window ${esc(w)}</h2>
        ${items.map(i => `<div class="pin"><span class="kind">${esc(i.kind)}</span> ${esc(i.summary)}${i.frame != null ? ` <span class="time">@ ${esc(ft(i.frame, fps))}</span>` : ''}</div>`).join('')}
      </section>`).join('')
    const html = `<!doctype html><html><head><title>${esc(gameCode)} — segment analysis</title><style>
      body { font: 13px/1.5 -apple-system, sans-serif; color: #111; margin: 32px; }
      h1 { font-size: 20px; margin: 0 0 4px; } .sub { color: #666; margin: 0 0 20px; }
      h2 { font-size: 14px; border-bottom: 1px solid #ccc; padding-bottom: 4px; margin: 20px 0 8px; }
      .pin { padding: 4px 0; } .kind { display: inline-block; min-width: 90px; color: #666; text-transform: uppercase; font-size: 10px; }
      .time { color: #888; }
    </style></head><body>
      <h1>${esc(gameCode)}</h1>
      <p class="sub">Segment analysis — ${pins.length} pinned item${pins.length === 1 ? '' : 's'} · exported ${new Date().toLocaleDateString()}</p>
      ${sections}
    </body></html>`
    const w = window.open('', '_blank', 'width=800,height=900')
    if (!w) return
    w.document.write(html)
    w.document.close()
    w.focus()
    w.print()
  }, [pins, gameCode, fps])

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return; if (e.key === 't' || e.key === 'T') { e.preventDefault(); setShowTagInput(true) } }
    document.addEventListener('keydown', h); return () => document.removeEventListener('keydown', h)
  }, [])

  // ── render ──
  if (!isStaff) return <p className="page-sub">Staff only.</p>
  if (!gameCode) return (
    <div className="seg-game-select">
      <div className="seg-select-row"><label>Club</label>
        <select value={selectedClub?.id ?? ''} onChange={e => { setSelectedClub(clubs?.find(c => c.id === Number(e.target.value)) ?? null); setGameCode(null) }}>
          <option value="">Select...</option>{clubs?.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
        </select></div>
      {clubGames && <div className="seg-select-row"><label>Game</label>
        <select value="" onChange={e => setGameCode(e.target.value || null)}>
          <option value="">Select...</option>{clubGames.map(g => <option key={g.game_code} value={g.game_code}>{g.game_code}</option>)}
        </select></div>}
    </div>
  )
  if (loading) return <div className="seg-loading">Loading {gameCode}...</div>
  if (error) return <div className="seg-error"><p>{error}</p><button onClick={() => setGameCode(null)}>Back</button></div>
  if (!gameData || !segWindow || fps === undefined) return <div className="seg-loading">Preparing...</div>

  const totalFrames = gameData.total_frames
  const wPct = { left: (segWindow.frameStart / totalFrames) * 100, width: ((segWindow.frameEnd - segWindow.frameStart) / totalFrames) * 100 }
  const c0 = gameData.away_team_color || '#3b82f6'
  const c1 = gameData.home_team_color || '#ef4444'

  return (
    <div className="seg-workbench">
      {/* Header */}
      <div className="seg-header">
        {!externalGameCode && <button className="seg-back" onClick={() => setGameCode(null)}>&larr;</button>}
        <span className="seg-game-label">{gameCode}</span>
        <span className="seg-window-label">
          <span className="seg-window-label__cap">Window</span>
          {wLabel(segWindow, fps)}
        </span>
        <span className="seg-window-adjust">
          <button onClick={() => setSegWindow({ ...segWindow, frameStart: Math.max(0, segWindow.frameStart - 30 * fps) })}>-30s</button>
          <button onClick={() => setSegWindow({ ...segWindow, frameEnd: Math.min(totalFrames, segWindow.frameEnd + 30 * fps) })}>+30s</button>
        </span>
        <span className="seg-ask">
          <input placeholder='Ask: "after the first goal", "best pressing spell"...'
                 value={askInput} onChange={e => setAskInput(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter') runAsk() }} />
          <button onClick={runAsk}>Go</button>
        </span>
        <button className="seg-share-btn" onClick={shareLink} title="Copy a link to this window and pinboard">Share</button>
      </div>

      {askChip && (
        <div className="seg-chip">
          <span>{askChip}</span>
          <button onClick={() => setAskChip(null)}>x</button>
        </div>
      )}

      {/* Full-match timeline with event markers */}
      <div className="seg-timeline" onClick={e => {
        const pct = (e.clientX - e.currentTarget.getBoundingClientRect().left) / e.currentTarget.getBoundingClientRect().width
        setWin(Math.floor(pct * totalFrames))
      }} title="Click to center window">
        <div className="seg-timeline-bar" />
        {/* Minute ticks every 5', labels every 10' — video-clock, like
            every frame number on this page. */}
        {Array.from({ length: Math.floor(totalFrames / (fps * 300)) }, (_, i) => (i + 1) * fps * 300).map(f => {
          const min = Math.round(f / fps / 60)
          return (
            <div key={f} className={`seg-tl-tick${min % 10 === 0 ? ' seg-tl-tick--major' : ''}`}
                 style={{ left: `${(f / totalFrames) * 100}%` }}>
              {min % 10 === 0 && <span>{min}&prime;</span>}
            </div>
          )
        })}
        <div className="seg-timeline-window" style={{ left: `${wPct.left}%`, width: `${wPct.width}%` }} />
        {halftime && <div className="seg-timeline-halftime" style={{ left: `${(halftime.startFrame / totalFrames) * 100}%` }} />}
        {/* Moment dots on full timeline */}
        {allMoments.filter(isHighScore).map((m, i) => {
          const f = m.frame ?? m.start_frame ?? 0
          return <div key={i} className="seg-tl-dot" style={{ left: `${(f / totalFrames) * 100}%` }}
                      title={`${m.type} ${ft(f, fps)}`} />
        })}
        {/* Corner markers */}
        {cornerMoments.map((c, i) => (
          <div key={`c${i}`} className="seg-tl-corner" style={{ left: `${((c.frame ?? 0) / totalFrames) * 100}%` }} />
        ))}
        {/* Goal markers */}
        {goalMoments.map((g, i) => (
          <div key={`g${i}`} className="seg-tl-goal" style={{ left: `${((g.frame ?? 0) / totalFrames) * 100}%` }}
               title={`Goal ${ft(g.frame ?? 0, fps)}`} />
        ))}
      </div>

      {/* Detail timeline (zoomed to window) */}
      <DetailTimeline window={segWindow} moments={windowAllMoments} thresholds={thresholds}
        tacticalScore={tacticalScoreData} pressingIndex={pressingIndexData}
        corners={cornerMoments} goals={goalMoments} fps={fps} team0Color={c0} team1Color={c1}
        onSeek={seekToFrame} />

      {/* Main panels */}
      <div className="seg-panels seg-panels--three">
        {/* Video + pitch control */}
        <div className="seg-panel seg-video-col">
          <div className="seg-video">
            {gameData.video_url ? (
              <video ref={videoRef} src={gameData.video_url} controls
                     onLoadedMetadata={() => { if (videoRef.current) videoRef.current.currentTime = (gameData.video_game_start_time || 0) + segWindow.frameStart / fps }} />
            ) : <div className="seg-no-video">No video</div>}
          </div>
          <div className="seg-spatial-panel">
            <PitchControlPanel cache={trackingCache} frameRef={liveFrameRef}
              fps={fps} team0Color={c0} team1Color={c1} />
          </div>
        </div>

        {/* Middle: the window BUILDING — networks and heat up to the playhead */}
        <div className="seg-panel seg-insights seg-live-col">
          <div className="seg-card">
            <h4 className="seg-card-title">Pass network &middot; live <span className="seg-rail-count">up to {ft(liveClamped, fps)}</span></h4>
            {buildingPassNetwork ? (
              <div className="seg-pass-nets">
                <div className="seg-pass-net">
                  <span className="seg-pass-net-label" style={{ color: c0 }}>{gameData.away_team}</span>
                  <PassNetSVG nodes={buildingPassNetwork.team0.nodes} edges={buildingPassNetwork.team0.edges} color={c0}
                              emptyNote={LIVE_NOT_YET} />
                </div>
                <div className="seg-pass-net">
                  <span className="seg-pass-net-label" style={{ color: c1 }}>{gameData.home_team}</span>
                  <PassNetSVG nodes={buildingPassNetwork.team1.nodes} edges={buildingPassNetwork.team1.edges} color={c1}
                              emptyNote={LIVE_NOT_YET} />
                </div>
              </div>
            ) : <p className="seg-empty">{LIVE_NOT_YET}</p>}
          </div>

          <div className="seg-card">
            <h4 className="seg-card-title">Heat &middot; live <span className="seg-rail-count">up to {ft(liveClamped, fps)}</span></h4>
            <div className="seg-heat-stack">
              <div className="seg-heat-row">
                <span className="seg-pass-net-label" style={{ color: c0 }}>{gameData.away_team}</span>
                <WindowHeatPanel cache={trackingCache} frameStart={segWindow.frameStart}
                  frameEnd={liveClamped} team={0} color={c0}
                  ariaLabel="Away heat, window so far" />
              </div>
              <div className="seg-heat-row">
                <span className="seg-pass-net-label" style={{ color: c1 }}>{gameData.home_team}</span>
                <WindowHeatPanel cache={trackingCache} frameStart={segWindow.frameStart}
                  frameEnd={liveClamped} team={1} color={c1}
                  ariaLabel="Home heat, window so far" />
              </div>
            </div>
          </div>
        </div>

        {/* Right: end-result maps + insight cards */}
        <div className="seg-panel seg-insights">
          {/* Copilot observations (Phase 3) */}
          <div className="seg-card">
            <h4 className="seg-card-title">Copilot <span className="seg-rail-count">{obsLoading ? 'computing...' : `${observations?.length ?? 0} observations`}</span></h4>
            {observations?.length ? (
              <div className="seg-obs-list">
                {observations.map((o, i) => (
                  <div key={i} className="seg-obs-row">
                    <span className="seg-obs-text">
                      {o.team != null && (
                        <span style={{ color: o.team === 1 ? c1 : c0, fontWeight: 600 }}>
                          {o.team === 1 ? gameData.home_team : gameData.away_team}:{' '}
                        </span>
                      )}
                      {o.text}
                    </span>
                    <button onClick={() => pinObservation(o)}>Pin</button>
                  </div>
                ))}
              </div>
            ) : obsNote ? <p className="seg-empty" role="status">{obsNote}</p>
              : !obsLoading && <p className="seg-empty">Nothing significant in this window.</p>}
          </div>

          {relatedSuggestion && (
            <div className="seg-chip seg-chip--suggest">
              <span>{relatedSuggestion.text}</span>
              <button onClick={() => { setWin(relatedSuggestion.frame); setRelatedSuggestion(null) }}>Open</button>
              <button onClick={() => setRelatedSuggestion(null)}>x</button>
            </div>
          )}
          {windowTactical && (
            <div className="seg-card">
              <h4 className="seg-card-title">Tactical score</h4>
              <div className="seg-stat-pair">
                <div className="seg-stat-col">
                  <span className="seg-stat-team" style={{ color: c1 }}>{gameData.home_team}</span>
                  <span className="seg-stat-value">{windowTactical.homeAvg}</span>
                  <span className="seg-stat-delta" data-dir={windowTactical.homeAvg >= windowTactical.homeMatchAvg ? 'up' : 'down'}>
                    {windowTactical.homeAvg >= windowTactical.homeMatchAvg ? '+' : ''}{windowTactical.homeAvg - windowTactical.homeMatchAvg}
                  </span>
                </div>
                <div className="seg-stat-col">
                  <span className="seg-stat-team" style={{ color: c0 }}>{gameData.away_team}</span>
                  <span className="seg-stat-value">{windowTactical.awayAvg}</span>
                  <span className="seg-stat-delta" data-dir={windowTactical.awayAvg >= windowTactical.awayMatchAvg ? 'up' : 'down'}>
                    {windowTactical.awayAvg >= windowTactical.awayMatchAvg ? '+' : ''}{windowTactical.awayAvg - windowTactical.awayMatchAvg}
                  </span>
                </div>
              </div>
            </div>
          )}

          {windowPressing && (
            <div className="seg-card">
              <h4 className="seg-card-title">Pressing</h4>
              <div className="seg-stat-row">
                <span className="seg-stat-value">{windowPressing.avg}%</span>
                <span className="seg-stat-label">pressing index</span>
                <span className="seg-stat-delta" data-dir={windowPressing.avg >= windowPressing.matchAvg ? 'up' : 'down'}>
                  {windowPressing.avg >= windowPressing.matchAvg ? '+' : ''}{windowPressing.avg - windowPressing.matchAvg}% vs match
                </span>
              </div>
              <div className="seg-stat-row">
                <span className="seg-stat-value">{windowPressing.regains}</span>
                <span className="seg-stat-label">regains</span>
              </div>
            </div>
          )}

          {windowPassNetwork && (
            <div className="seg-card">
              <h4 className="seg-card-title">Pass network &middot; whole window <span className="seg-rail-count">{windowPassNetwork.passes.length} passes</span></h4>
              <div className="seg-pass-nets">
                <div className="seg-pass-net">
                  <span className="seg-pass-net-label" style={{ color: c0 }}>{gameData.away_team}</span>
                  <PassNetSVG nodes={windowPassNetwork.team0.nodes} edges={windowPassNetwork.team0.edges} color={c0} />
                </div>
                <div className="seg-pass-net">
                  <span className="seg-pass-net-label" style={{ color: c1 }}>{gameData.home_team}</span>
                  <PassNetSVG nodes={windowPassNetwork.team1.nodes} edges={windowPassNetwork.team1.edges} color={c1} />
                </div>
              </div>
            </div>
          )}

          {/* Moment type breakdown */}
          <div className="seg-card">
            <h4 className="seg-card-title">Activity <span className="seg-rail-count">{windowMoments.length} moments (score {MIN_SCORE_SHOWN}+)</span></h4>
            {momentBreakdown.length > 0 ? (
              <div className="seg-breakdown">
                {momentBreakdown.map(([t, c]) => (
                  <div key={t} className="seg-breakdown-row">
                    <span className="seg-breakdown-label">{t.replace(/_/g, ' ')}</span>
                    <span className="seg-breakdown-count">{c}</span>
                    <div className="seg-breakdown-bar" style={{ width: `${(c / windowMoments.length) * 100}%` }} />
                  </div>
                ))}
              </div>
            ) : <p className="seg-empty">No high-score moments in window.</p>}
          </div>

          {/* Per-team heat maps over the FULL window — stacked vertically */}
          <div className="seg-card">
            <h4 className="seg-card-title">Heat &middot; whole window <span className="seg-rail-count">{wLabel(segWindow, fps)}</span></h4>
            <div className="seg-heat-stack">
              <div className="seg-heat-row">
                <span className="seg-pass-net-label" style={{ color: c0 }}>{gameData.away_team}</span>
                <WindowHeatPanel cache={trackingCache} frameStart={segWindow.frameStart}
                  frameEnd={segWindow.frameEnd} team={0} color={c0}
                  ariaLabel="Away heat, full window" />
              </div>
              <div className="seg-heat-row">
                <span className="seg-pass-net-label" style={{ color: c1 }}>{gameData.home_team}</span>
                <WindowHeatPanel cache={trackingCache} frameStart={segWindow.frameStart}
                  frameEnd={segWindow.frameEnd} team={1} color={c1}
                  ariaLabel="Home heat, full window" />
              </div>
            </div>
          </div>

          {/* Moments list (inline in insights column) */}
          <div className="seg-card">
            <h4 className="seg-card-title">Moments <span className="seg-rail-count">{windowMoments.length} (score {MIN_SCORE_SHOWN}+)</span></h4>
            <div className="seg-moments-list">
              {!windowMoments.length && <p className="seg-empty">No moments above {MIN_SCORE_SHOWN}.</p>}
              {windowMoments.map((m, i) => {
                const f = m.frame ?? m.start_frame ?? 0
                return (
                  <div key={m.moment_id ?? m.id ?? i} className="seg-moment-card">
                    <div className="seg-moment-top">
                      <span className="seg-moment-score">{scoreText(m)}</span>
                      <span className="seg-moment-type">{m.type?.replace(/_/g, ' ')}</span>
                      <span className="seg-moment-time">{ft(f, fps)}</span>
                    </div>
                    {m.player != null && <span className="seg-moment-player">#{m.player}</span>}
                    <div className="seg-moment-actions">
                      <button onClick={() => seekToFrame(f)}>Play</button>
                      <button onClick={() => pinMoment(m)}>Pin</button>
                      <button onClick={() => setWin(f)}>Focus</button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {showTagInput ? (
            <div className="seg-tag-input">
              <input autoFocus placeholder="Tag..." value={tagInput} onChange={e => setTagInput(e.target.value)}
                     onKeyDown={e => { if (e.key === 'Enter' && tagInput.trim()) { pinTag(tagInput.trim()); setTagInput(''); setShowTagInput(false) }; if (e.key === 'Escape') { setShowTagInput(false); setTagInput('') } }} />
            </div>
          ) : <button className="seg-tag-btn" onClick={() => setShowTagInput(true)}>Tag (T)</button>}
        </div>
      </div>

      {pins.length > 0 && (
        <div className="seg-pinboard">
          <div className="seg-pinboard-header">
            <h3>Pinboard ({pins.length})</h3>
            <button onClick={exportPins}>Copy</button>
            <button onClick={exportPdf}>PDF</button>
          </div>
          <div className="seg-pinboard-items">
            {pins.map(p => (
              <div key={p.id} className="seg-pin-card">
                <div className="seg-pin-top"><span className="seg-pin-kind">{p.kind}</span><span className="seg-pin-window">{p.windowLabel}</span><button className="seg-pin-remove" onClick={() => unpin(p.id)}>x</button></div>
                <div className="seg-pin-summary">{p.summary}</div>
                {p.frame != null && <button className="seg-pin-play" onClick={() => seekToFrame(p.frame!)}>Play</button>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
